/**
 * Anotar el marcador de un partido.
 *
 * QUIÉN LO USA Y DÓNDE: alguien parado al lado de la cancha, con el teléfono
 * en una mano y a veces con sol de frente. Todo acá está pensado para eso:
 * campos grandes, teclado numérico, un set a la vez, y el error explicado en
 * una frase antes de que se pueda guardar nada.
 *
 * LOS NÚMEROS VAN DE 0 A 10, y no de 0 a 6, porque el mismo campo guarda un
 * set normal (6-4, 7-5) y un súper tie break (10-8), que es como se resuelve
 * el tercer set en casi todos los torneos de pádel de acá. La consecuencia es
 * que un 8-3 pasa: no hay forma de distinguirlo sin preguntar en cada set qué
 * tipo era, y eso no se le pregunta a alguien que está anotando en la cancha.
 *
 * LA COMPROBACIÓN ES LA MISMA QUE LA DEL SERVIDOR, literalmente la misma
 * función del contrato. Sirve para decirlo en el acto en vez de después de
 * ir y volver; quien decide sigue siendo el servidor.
 *
 * Y EL PARTIDO QUE NO SE JUGÓ. Cuando el rival no llega, no hay sets que
 * anotar y el partido igual tiene que resolverse: se elige quién pasa, y se
 * guarda quién FALTÓ. Es un modo aparte y no un marcador inventado tipo 6-0
 * 6-0, porque un 6-0 6-0 en el historial de alguien dice que jugó y perdió,
 * y no es lo que pasó.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMMENT_MAX,
  SCORE_LIMITS,
  checkScore,
  type Match,
  type MatchScore,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { ImagePicker } from './ImagePicker';
import { assetUrl } from '../lib/api';

/**
 * UNO DE LOS DOS QUE JUGARON.
 *
 * `id` y `name` y nada más: es todo lo que el editor necesita saber de una
 * persona —a quién nombrar en la tabla y qué mandar cuando faltó— y pedirle
 * un `TournamentEntry` entero lo ataría otra vez a los torneos.
 */
export interface ScoreSide {
  id: string;
  name: string;
  /**
   * La foto, para reconocer de un vistazo de quién es cada fila.
   *
   * Opcional porque no todas las pantallas la tienen a mano, y porque la
   * tabla se entiende igual sin ella: el nombre está al lado.
   */
  avatarPath?: string | null;
}

/** Los datos de un partido de torneo, con la forma que este editor pide. */
export function sidesOfMatch(match: Match): {
  home: ScoreSide | null;
  away: ScoreSide | null;
  score: MatchScore;
  noShowId: string | null;
  photoPath: string | null;
  photoKey: string;
} {
  return {
    home:
      match.home === null
        ? null
        : { id: match.home.id, name: match.home.displayName, avatarPath: match.home.avatarPath },
    away:
      match.away === null
        ? null
        : { id: match.away.id, name: match.away.displayName, avatarPath: match.away.avatarPath },
    score: match.score,
    noShowId: match.noShowEntryId,
    photoPath: match.photoPath,
    photoKey: match.id,
  };
}

interface ScoreEditorProps {
  home: ScoreSide | null;
  away: ScoreSide | null;
  score: MatchScore;
  /** Quién NO se presentó, si el partido no se jugó. */
  noShowId: string | null;
  photoPath?: string | null;
  /** Para que el campo de la foto tenga un id único en la pantalla. */
  photoKey?: string;
  /**
   * Quién faltó viaja junto al marcador y no por otro camino: las dos cosas
   * contestan la misma pregunta —cómo terminó esto— y guardarlas por separado
   * daría dos formas de resolverlo que se pueden contradecir.
   */
  /**
   * ¿PUEDE QUEDAR EMPATADO? Apagado de fábrica.
   *
   * Un cuadro de torneo no puede: alguien avanza. Solo los retos amistosos lo
   * prenden. Se le pasa a `checkScore`, que es la misma función que aplica el
   * servidor, así que la pantalla nunca ofrece algo que después rebota.
   */
  allowDraw?: boolean;
  /**
   * ¿SE PREGUNTA SI EL ÚLTIMO SET FUE UN SÚPER TIE BREAK?
   *
   * Apagado de fábrica: los partidos de torneo se anotan igual que siempre y
   * esta pregunta solo aplica donde el club juega a súper tie break.
   */
  askSuperTieBreak?: boolean;
  /** Lo que ya estaba marcado, al corregir un marcador. */
  superTieBreak?: boolean;
  /**
   * ¿EXISTE EL «no se presentó»? Prendido de fábrica.
   *
   * Los retos todavía no lo aceptan del lado del servidor, así que ahí se
   * apaga: un interruptor que lleva a un error no es un interruptor, es una
   * trampa.
   */
  allowNoShow?: boolean;
  /**
   * `winner` es quién ganó SEGÚN EL MARCADOR, o `null` si empataron o si fue
   * W.O. Viaja calculado desde acá y no se recalcula afuera: es el mismo
   * `checkScore` que dibuja la línea de «gana Fulano», y dos cuentas del
   * mismo hecho es cómo terminan diciendo cosas distintas.
   *
   * Un partido de torneo lo ignora —el servidor lo deduce del marcador— y un
   * reto lo necesita, porque manda el ganador junto con el marcador.
   */
  onSave: (
    score: MatchScore,
    noShowId: string | null,
    winner: 'home' | 'away' | null,
    /** El último set fue un súper tie break. `false` si no se preguntó. */
    superTieBreak: boolean,
  ) => Promise<void>;
  onCancel: () => void;
  /**
   * La foto del partido. `null` la quita.
   *
   * SE SUBE APARTE DEL MARCADOR y no junto con él: son dos cosas distintas
   * —una es el resultado, la otra un recuerdo— y atarlas obligaría a cargar
   * el marcador otra vez para cambiar la foto, o a subir una foto para poder
   * corregir un set. Sin esta función, la caja de la foto no aparece.
   */
  onPhoto?: (file: File | null) => Promise<void>;
  /**
   * BORRAR EL MARCADOR, por su propio camino.
   *
   * ANTES ESTE BOTÓN MANDABA `onSave([], null)`, o sea que borraba por la
   * ruta de ANOTAR. Funcionaba, pero significaba que quien podía anotar podía
   * borrar — y en un club que deja anotar a los jugadores, eso permitía que
   * alguien borrara el marcador de su propio partido y, con él, el avance de
   * otros por el cuadro. Ahora borrar tiene su ruta y su permiso.
   *
   * SIN ESTA FUNCIÓN NO HAY BOTÓN. Es como se apaga para quien no puede: el
   * servidor lo dice en `canClearScore`, y quien dibuja el editor decide si
   * la pasa.
   */
  onClear?: () => Promise<void>;
  /**
   * DEJAR DICHO ALGO DEL PARTIDO, en el mismo acto de anotarlo.
   *
   * Se llama DESPUÉS de `onSave` y solo si hay texto. El orden importa: en un
   * reto no se puede comentar hasta que está jugado —el servidor lo exige, y
   * tiene razón: comentar algo que todavía no pasó no es comentar nada— así
   * que primero queda el resultado y después la frase.
   *
   * ESTÁ ACÁ Y NO EN UNA CAJA APARTE porque es un solo acto: se sale de la
   * cancha, se anota el 6-4 y se dice «partidazo». Con dos pasos, el segundo
   * no lo hace nadie.
   *
   * Si no viene, no se dibuja: un partido de cuadro sin muro donde comentar
   * no tiene dónde poner esa frase.
   */
  onComment?: (body: string) => Promise<void>;
}

/** Un set a medio escribir: los campos pueden estar vacíos mientras se teclea. */
interface DraftSet {
  home: string;
  away: string;
}

/** De lo escrito a un marcador, descartando los sets que quedaron en blanco. */
export function toScore(sets: readonly DraftSet[]): MatchScore {
  return sets
    .filter((set) => set.home !== '' || set.away !== '')
    .map((set) => ({ home: Number(set.home), away: Number(set.away) }));
}

/** `true` si algún set quedó a medias: un número puesto y el otro no. */
export function hasHalfSet(sets: readonly DraftSet[]): boolean {
  return sets.some((set) => (set.home === '') !== (set.away === ''));
}

export function ScoreEditor({
  home: ladoHome,
  away: ladoAway,
  score,
  noShowId,
  photoPath = null,
  photoKey = 'match',
  allowDraw = false,
  askSuperTieBreak = false,
  superTieBreak: superTieBreakInicial = false,
  allowNoShow = true,
  onSave,
  onCancel,
  onPhoto,
  onClear,
  onComment,
}: ScoreEditorProps): JSX.Element {
  const { t } = useTranslation();
  const [sets, setSets] = useState<DraftSet[]>(() => desde(score));
  const [sending, setSending] = useState(false);
  /** Si el botón de borrar ya preguntó. Ver el botón, más abajo. */
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Lo que se quiere dejar dicho del partido. Ver `onComment`. */
  const [comentario, setComentario] = useState('');
  /** `'wo'` = el rival no llegó. Se entra con el interruptor de abajo. */
  const [modo, setModo] = useState<'sets' | 'wo'>(noShowId === null ? 'sets' : 'wo');
  const [superTieBreak, setSuperTieBreak] = useState(superTieBreakInicial);
  /**
   * QUIÉN PASA, que es la pregunta que se hace en la cancha. Adentro se
   * guarda quién faltó —el otro—, porque quién pasó ya se deduce y tener las
   * dos caras del mismo dato es cómo terminan contradiciéndose.
   */
  const [pasa, setPasa] = useState<string | null>(() =>
    noShowId === null
      ? null
      : noShowId === ladoHome?.id
        ? ladoAway?.id ?? null
        : ladoHome?.id ?? null,
  );

  const marcador = toScore(sets);
  const medias = hasHalfSet(sets);
  const revisado = marcador.length === 0 ? null : checkScore(marcador, { allowDraw });
  const problema = medias
    ? t('score.halfSet')
    : revisado !== null && !revisado.ok
      ? t(`scoreProblem.${revisado.problem}`)
      : null;

  const home = ladoHome?.name ?? '—';
  const away = ladoAway?.name ?? '—';
  /** El nombre de quien gana, o `null` si todavía no se sabe o si empataron. */
  const ganador =
    revisado !== null && revisado.ok && revisado.outcome.winner !== null
      ? revisado.outcome.winner === 'home'
        ? home
        : away
      : null;
  /** `true` cuando el marcador está completo Y no lo gana nadie. */
  const empatado = revisado !== null && revisado.ok && revisado.outcome.winner === null;

  function cambiar(index: number, lado: 'home' | 'away', valor: string): void {
    // Solo dígitos, y como mucho dos: nada de un 66 que en realidad era 6.
    const limpio = valor.replace(/\D/g, '').slice(0, 2);
    setSets((actual) => actual.map((set, i) => (i === index ? { ...set, [lado]: limpio } : set)));
  }

  async function guardar(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      if (modo === 'wo') {
        // Se manda quién FALTÓ: el que no es el que pasa.
        const faltó = pasa === ladoHome?.id ? ladoAway?.id : ladoHome?.id;
        // UN WALKOVER NO TIENE SETS, así que tampoco tie break: va `false`
        // aunque el check estuviera marcado de antes.
        await onSave([], faltó ?? null, null, false);
      } else {
        await onSave(
          marcador,
          null,
          revisado !== null && revisado.ok ? revisado.outcome.winner : null,
          superTieBreak,
        );
      }

      /*
       * EL COMENTARIO VA DESPUÉS DEL MARCADOR, Y SI FALLA NO DESHACE NADA.
       *
       * El resultado ya quedó guardado, que es lo que importa; perder la
       * frase es molesto y perder el 6-4 sería grave. Se avisa y el editor se
       * queda abierto con el texto puesto, para poder volver a intentarlo sin
       * tener que acordarse de lo que uno había escrito.
       */
      const dicho = comentario.trim();
      if (dicho !== '' && onComment !== undefined) {
        await onComment(dicho);
        setComentario('');
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.UNKNOWN'));
    } finally {
      /*
       * `finally` Y NO SOLO EN EL `catch`, QUE ES COMO ESTABA.
       *
       * En el camino de ÉXITO nunca se apagaba: el botón se quedaba en
       * «Enviando…» para siempre con el marcador YA GUARDADO. Casi nunca se
       * veía porque quien llama recarga la lista y este editor se desmonta
       * antes —el botón colgado se va con él—, pero donde el editor sigue en
       * pantalla queda trabado y parece que la aplicación se colgó.
       *
       * Javier lo encontró corrigiendo un amistoso: «Cuando reporto el
       * marcador se queda enviando». El resultado se había guardado bien.
       */
      setSending(false);
    }
  }

  return (
    <div className="score-editor">
      <h3>{t('score.title')}</h3>
      <p className="help">{t('score.help', { max: SCORE_LIMITS.gamesMax })}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {modo === 'wo' ? (
        <WalkoverPicker
          lados={[ladoHome, ladoAway]}
          pasa={pasa}
          disabled={sending}
          onPick={setPasa}
        />
      ) : (
      <table className="score-table">
        <caption className="visually-hidden">{t('score.title')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('score.player')}</th>
            {sets.map((_, i) => (
              <th scope="col" key={i}>
                {t('score.setN', { n: i + 1 })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(['home', 'away'] as const).map((lado) => (
            <tr key={lado}>
              {/* LA FOTO AL LADO DEL NOMBRE, como en la pantalla que mandó
                  Javier: en una tabla de dos filas casi idénticas, la cara es
                  lo que se mira para saber cuál renglón es cuál antes de
                  escribir un número. Sin foto se dibuja la inicial, que es lo
                  que hace `Avatar`. */}
              <th scope="row">
                <span className="score-who">
                  <Avatar
                    url={assetUrl((lado === 'home' ? ladoHome : ladoAway)?.avatarPath ?? null)}
                    name={lado === 'home' ? home : away}
                    size={28}
                  />
                  <span className="score-who-name">{lado === 'home' ? home : away}</span>
                </span>
              </th>
              {sets.map((set, i) => (
                <td key={i}>
                  <input
                    // `inputMode="numeric"` y no `type="number"`: el de número
                    // cambia el valor con la rueda del mouse encima del campo
                    // sin que nadie lo note, y en el celular abre un teclado
                    // con flechitas que acá no sirven para nada.
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="score-cell"
                    disabled={sending}
                    aria-label={t('score.setOf', {
                      n: i + 1,
                      name: lado === 'home' ? home : away,
                    })}
                    value={set[lado]}
                    onChange={(e) => cambiar(i, lado, e.target.value)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      )}

      {/*
        ¿EL ÚLTIMO SET FUE UN SÚPER TIE BREAK?

        Javier: «agreguemos un check si el último set es un tie break al
        momento de reportar el marcador».

        SOLO EN MODO SETS Y CON MÁS DE UN SET. Con un set solo no hay «último
        set» que distinguir del resto, y en un walkover no se jugó nada.

        SE PREGUNTA Y NO SE DEDUCE del marcador: se juegan a 7 y a 10 según el
        club, así que un 7-5 puede ser tie break y un tercer set normal puede
        terminar 10-8. Adivinarlo mete errores en una estadística que después
        nadie va a poder corregir.
      */}
      {askSuperTieBreak && modo === 'sets' && marcador.length > 1 && (
        <p className="button-row">
          <label className="check">
            <input
              type="checkbox"
              checked={superTieBreak}
              disabled={sending}
              onChange={(e) => setSuperTieBreak(e.target.checked)}
            />
            {t('score.superTieBreak')}
          </label>
        </p>
      )}

      {/* EL INTERRUPTOR VA DEBAJO DE LO NORMAL, no arriba: casi todos los
          partidos se juegan, y lo raro no puede estar delante de lo común.
          Solo aparece con los dos jugadores puestos: sin rival no hay nadie
          que pueda faltar. */}
      {allowNoShow && ladoHome !== null && ladoAway !== null && (
        <p className="button-row">
          <button
            type="button"
            className="link"
            disabled={sending}
            onClick={() => setModo(modo === 'wo' ? 'sets' : 'wo')}
          >
            {modo === 'wo' ? t('score.backToSets') : t('score.noShow')}
          </button>
        </p>
      )}

      <div className="button-row">
        {modo === 'sets' && sets.length < SCORE_LIMITS.setsMax && (
          <button
            type="button"
            className="link"
            disabled={sending}
            onClick={() => setSets((actual) => [...actual, { home: '', away: '' }])}
          >
            {t('score.addSet')}
          </button>
        )}
        {modo === 'sets' && sets.length > 1 && (
          <button
            type="button"
            className="link"
            disabled={sending}
            onClick={() => setSets((actual) => actual.slice(0, -1))}
          >
            {t('score.removeSet')}
          </button>
        )}
      </div>

      {modo === 'sets' && problema !== null && <Alert kind="error">{problema}</Alert>}

      {/* Quién gana, dicho con todas las letras antes de guardar. Es la
          comprobación que hace la persona: si ahí dice el nombre equivocado,
          los sets están al revés. */}
      {modo === 'sets' && ganador !== null && (
        <p className="score-winner" role="status">
          {t('score.willWin', { name: ganador })}
        </p>
      )}

      {/* Y EL EMPATE TAMBIÉN SE DICE. Un marcador válido que no dibuja
          ninguna línea se lee como que falta algo, cuando lo que pasa es que
          no lo gana nadie. */}
      {modo === 'sets' && empatado && (
        <p className="score-winner" role="status">
          {t('score.willDraw')}
        </p>
      )}

      {onPhoto !== undefined && (
        <MatchPhotoBox
          photoKey={photoKey}
          photoPath={photoPath}
          onPhoto={onPhoto}
        />
      )}

      {/*
        LA FRASE DEL PARTIDO, EN EL MISMO FORMULARIO.
        Un renglón y no una caja de varios: lo que se escribe al salir de la
        cancha es «partidazo» o «lo di vuelta en el tercero», no un párrafo.
        Va antes de los botones porque se escribe antes de guardar.
      */}
      {onComment !== undefined && (
        <p className="field score-say">
          <label htmlFor={`score-say-${photoKey}`}>{t('score.say')}</label>
          <input
            id={`score-say-${photoKey}`}
            type="text"
            autoComplete="off"
            maxLength={COMMENT_MAX}
            placeholder={t('wall.commentPlaceholder')}
            value={comentario}
            disabled={sending}
            onChange={(event) => setComentario(event.target.value)}
          />
          <span className="help">{t('score.sayHelp')}</span>
        </p>
      )}

      <div className="button-row">
        <button
          type="button"
          /*
           * GUARDAR NO GUARDA NADA CUANDO NO HAY NADA. El campo vacío no es
           * «borrá el resultado»: para eso está el botón de al lado, que lo
           * dice con esas palabras. Sin esta condición, tocar Guardar en un
           * formulario en blanco mandaba un marcador vacío — que en un
           * partido de torneo borraba el resultado sin avisar, y en un reto
           * rebotaba con un error del servidor.
           */
          disabled={
            sending ||
            (modo === 'wo' ? pasa === null : problema !== null || marcador.length === 0)
          }
          onClick={() => void guardar()}
        >
          {sending ? t('common.sending') : t('common.save')}
        </button>
        {/*
          BORRAR EL MARCADOR, EN DOS PASOS.

          PIDE CONFIRMACIÓN, y es de las pocas cosas de la aplicación que lo
          hacen: esto le saca puntos a dos personas que no están mirando esta
          pantalla, y en un cuadro además puede sacar a alguien de la ronda
          siguiente. Un toque sin querer no puede hacer eso.

          SOLO SI `onClear` VINO: es como se apaga para quien no tiene el
          permiso. Ver `canClearScore`.
        */}
        {onClear !== undefined && (score.length > 0 || noShowId !== null) && (
          borrando ? (
            <>
              <button
                type="button"
                className="link danger"
                disabled={sending}
                onClick={() => {
                  setBorrando(false);
                  setSets([{ home: '', away: '' }]);
                  setPasa(null);
                  setModo('sets');
                  // Sin sets y sin ausencia: el partido vuelve a estar sin
                  // jugar. Y sin tie break, que sin sets no pudo existir.
                  setSuperTieBreak(false);
                  void onClear();
                }}
              >
                {t('score.clearYes')}
              </button>
              <button
                type="button"
                className="link"
                disabled={sending}
                onClick={() => setBorrando(false)}
              >
                {t('common.cancel')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="link danger"
              disabled={sending}
              onClick={() => setBorrando(true)}
            >
              {t('score.clear')}
            </button>
          )
        )}
        <button type="button" className="link" disabled={sending} onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * LA FOTO DEL PARTIDO: la del final, o el marcador escrito en la pizarra.
 *
 * SE SUBE SOLA, apenas se elige, y no con el botón de guardar el marcador.
 * Son dos cosas distintas: atarlas obligaría a cargar el marcador de nuevo
 * para cambiar la foto, o a poner una foto para poder corregir un set.
 *
 * UNA SOLA FOTO: elegir otra reemplaza la que había. Es lo que uno espera de
 * «la foto del partido», y guardar varias obliga a decidir cuál se muestra.
 */
function MatchPhotoBox({
  photoKey,
  photoPath,
  onPhoto,
}: {
  photoKey: string;
  photoPath: string | null;
  onPhoto: (file: File | null) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [elegida, setElegida] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function mandar(file: File | null): Promise<void> {
    setSubiendo(true);
    setError(null);
    try {
      await onPhoto(file);
      setElegida(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.UNKNOWN'));
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div className="match-photo">
      {error !== null && <Alert kind="error">{error}</Alert>}

      <ImagePicker
        id={`match-photo-${photoKey}`}
        label={t('score.photo')}
        help={t('score.photoHelp')}
        currentUrl={assetUrl(photoPath)}
        value={elegida}
        onChange={(file) => {
          setElegida(file);
          // Se sube sola: pedir un segundo toque para confirmar una foto que
          // ya se ve en pantalla es un paso que no decide nada.
          if (file !== null) void mandar(file);
        }}
        disabled={subiendo}
      />

      {photoPath !== null && (
        <button
          type="button"
          className="link danger"
          disabled={subiendo}
          onClick={() => void mandar(null)}
        >
          {t('score.removePhoto')}
        </button>
      )}
    </div>
  );
}

/**
 * QUIÉN PASA cuando el partido no se jugó.
 *
 * Se pregunta por quién PASA y no por quién faltó, porque es como se dice en
 * la cancha —«pasa Beto»— y porque elegir a la persona equivocada en la
 * pregunta negativa es un error fácil de cometer y difícil de notar. Lo que
 * se guarda es el otro.
 *
 * Son botones de radio de verdad y no dos botones: es una elección entre dos,
 * excluyente, y el teclado y el lector de pantalla ya saben leer eso.
 */
function WalkoverPicker({
  lados: crudos,
  pasa,
  disabled,
  onPick,
}: {
  lados: ReadonlyArray<ScoreSide | null>;
  pasa: string | null;
  disabled: boolean;
  onPick: (sideId: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const lados = crudos.filter((e): e is ScoreSide => e !== null);

  return (
    <fieldset className="plain-fieldset">
      <legend>{t('score.whoAdvances')}</legend>
      <p className="help">{t('score.noShowHelp')}</p>
      <div className="choice-group">
        {lados.map((lado) => (
          <label key={lado.id} className="choice">
            <input
              type="radio"
              name="pasa"
              value={lado.id}
              disabled={disabled}
              checked={pasa === lado.id}
              onChange={() => onPick(lado.id)}
            />
            <span>{lado.name}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Lo guardado, listo para editar. Un partido sin jugar arranca con un set. */
function desde(score: MatchScore): DraftSet[] {
  if (score.length === 0) return [{ home: '', away: '' }];
  return score.map((set) => ({ home: String(set.home), away: String(set.away) }));
}
